import { useCallback } from "react";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { _sessionManager } from "api/ApiHelper";

/**
 * Hook for handling subscription payment-related actions
 */
export const usePaymentHandling = (
  activeProducer,
  activeAgent,
  { agent_token, is_broker },
  globalDispatch,
  handleError,
  setDoShowPaymentDialog,
  setIsLoadingPaymentLink,
  setIsLoadingPaperCheck
) => {
  const handlePaymentSnooze = useCallback(() => {
    setDoShowPaymentDialog(false);
    globalDispatch({
      type: "update",
      payload: {
        key: "subscription_snooze_timestamp",
        value: new Date().getTime(),
      },
    });
  }, [globalDispatch, setDoShowPaymentDialog]);

  const handlePayment = useCallback(
    async (selectedInvoices) => {
      setIsLoadingPaymentLink(true);
      try {
        const invoiceNumbers = (selectedInvoices || []).map((s) => s.invoicenumber);
        const payTotal = (selectedInvoices || []).reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);

        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          _instance: null,
        });

        // Mirror the (working) read path: source tokens from the active member row
        // and omit user_role so addAuthorizationKeys does not force the self-id
        // producer_token onto the body. For an agent paying their own agency the
        // member-row producer_token is null, which the backend authorizes without
        // requiring a self UserGrant.
        const currentMember = activeProducer || activeAgent;
        const memberProducerToken = currentMember?.producer_token ?? null;
        const memberAgentToken = currentMember?.agent_token ?? null;

        const url = await api.callPost(
          "Member/invoices/payment/getSessionUrl",
          {
            producer_token: memberProducerToken,
            agent_token: memberAgentToken,
            invoice_numbers: invoiceNumbers,
            pay_total: payTotal,
          },
          { producer_token: memberProducerToken, agent_token: memberAgentToken }
        );
        window.open(url, "_blank", "noopener,noreferrer");
        setDoShowPaymentDialog(false);
        globalDispatch({
          type: "update",
          payload: {
            key: "subscription_payment_timestamp",
            value: new Date().getTime(),
          },
        });
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoadingPaymentLink(false);
      }
    },
    [activeProducer, activeAgent, globalDispatch, handleError, setDoShowPaymentDialog, setIsLoadingPaymentLink]
  );

  const handlePaperCheck = useCallback(
    async (selectedInvoices) => {
      setIsLoadingPaperCheck(true);
      try {
        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          _instance: null,
        });

        const invoiceNumbers = (selectedInvoices || []).map((s) => s.invoicenumber);

        // Same token context as handlePayment / the read path (see note above).
        const currentMember = activeProducer || activeAgent;
        const memberProducerToken = currentMember?.producer_token ?? null;
        const memberAgentToken = currentMember?.agent_token ?? null;

        await api.callPost(
          "Member/invoices/payment/check",
          {
            producer_token: memberProducerToken,
            agent_token: memberAgentToken,
            invoice_numbers: invoiceNumbers,
          },
          { producer_token: memberProducerToken, agent_token: memberAgentToken }
        );

        setDoShowPaymentDialog(false);
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoadingPaperCheck(false);
      }
    },
    [activeProducer, activeAgent, globalDispatch, handleError, setDoShowPaymentDialog, setIsLoadingPaperCheck]
  );

  const handleActiveUserSignoutClick = useCallback(() => {
    globalDispatch({
      type: "setProducer",
      payload: {
        producer_token: null,
        agent_token,
      },
    });

    if (is_broker) {
      return "/broker/clients";
    } else {
      return "/account";
    }
  }, [globalDispatch, agent_token, is_broker]);

  return {
    handlePaymentSnooze,
    handlePayment,
    handlePaperCheck,
    handleActiveUserSignoutClick,
  };
};
